import { ApplicationConfig, inject, provideAppInitializer, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { provideClientHydration, withEventReplay, withHttpTransferCacheOptions, withIncrementalHydration } from '@angular/platform-browser';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { IMAGE_LOADER } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { routes } from './app.routes';
import { strapiAuthInterceptor } from './core/api/strapi-auth.interceptor';
import { stripHiddenSectionsInterceptor } from './core/api/strip-hidden-sections.interceptor';
import { BLOCK_REGISTRY } from './shared/organisms/dynamic-section/dynamic-section';
import { COMPONENT_REGISTRY } from './core/dynamic-zone/component-registry';
import { NavigationService } from './core/services/navigation.service';
import { vercelImageLoader } from './image-loader';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' }),
      // quick-109 — every in-app nav must push a real history entry.
      // Angular's default is `onSameUrlNavigation: 'ignore'`, which silently
      // swallows a router.navigate(...) call when the target URL string
      // (path + query + fragment) equals the current one. That swallow path
      // is what masks the back-button regression: SameOriginLinkDirective
      // calls `router.navigate([path], { fragment, queryParams })` for every
      // same-origin <a> click, and any redundant click on a link already
      // matching the active URL (logo while on '/', a 'Home' nav item that
      // resolves to the same href as the logo, the skip-link '#main' while
      // on the same path, etc.) would skip pushing a history entry under
      // the default. 'reload' is the documented Angular setting that
      // guarantees a fresh nav lifecycle (and a real history push) on every
      // click, restoring `page A → /home → /contact` ⇒ Back lands on /home.
      // Safe by construction: identical-URL re-navigations re-run resolvers,
      // which is what the user intuitively expects from clicking a nav link.
      // Does NOT interact with the three intentional replaceUrl/
      // skipLocationChange sites in cms-page.resolver.ts (redirect 348,
      // canonical 374, 404 395) — those use navigateByUrl/navigate with
      // explicit extras that this option does not override.
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
    ),
    provideClientHydration(
      withEventReplay(),
      withIncrementalHydration(),
      withHttpTransferCacheOptions({
        includeRequestsWithAuthHeaders: true,
      }),
    ),
    provideHttpClient(
      withFetch(),
      withInterceptors([strapiAuthInterceptor, stripHiddenSectionsInterceptor]),
    ),
    // Force navigation to resolve during bootstrap so SSR fetches it (and the
    // HTTP transfer cache captures it into TransferState) before the header
    // template renders. Without this, `toSignal(navigation$, { initialValue:
    // null })` returns null synchronously during SSR — the header's
    // `@for (item of navItems()...)` renders nothing, the GET never fires
    // server-side, and the client makes a fresh /api/navigation call on
    // hydration (~800ms blocking LCP). With it, SSR awaits the value, the
    // cache picks it up, and the client gets nav from TransferState.
    //
    // Fault-tolerant: a failed fetch (Strapi unreachable during build's
    // route-extraction step, transient upstream 5xx, etc.) must NOT block
    // bootstrap — the header templates already handle `navigation()` being
    // null gracefully. Catch + swallow keeps the app bootable in degraded
    // state and matches the "render whatever we can" SSR resilience model.
    provideAppInitializer(() =>
      firstValueFrom(inject(NavigationService).navigation$).catch(() => null),
    ),
    { provide: BLOCK_REGISTRY, useValue: COMPONENT_REGISTRY },
    { provide: IMAGE_LOADER, useValue: vercelImageLoader },
  ],
};
